'use client';

import { useState, useEffect } from 'react';
import { X, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getOverrideModeFromCookie, type AppMode } from '@/lib/app-mode';

const MODE_LABELS: Record<AppMode, string> = {
  'development': 'Development',
  'pre-launch': 'Pre-Launch',
  'launch-ready': 'Launch Ready',
  'post-launch': 'Post-Launch',
  'production': 'Production',
};

/**
 * Small fixed banner shown when a preview mode override cookie is active.
 * Lets testers know which mode they're viewing and provides a clear link.
 */
export function PreviewModeBanner() {
  const [overrideMode, setOverrideMode] = useState<AppMode | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setOverrideMode(getOverrideModeFromCookie());
  }, []);

  if (!overrideMode || dismissed) return null;

  const handleClear = () => {
    // Navigate to clear the cookie via middleware
    window.location.href = '/?preview_mode=clear';
  };

  return (
    <div
      className={cn(
        'fixed bottom-20 lg:bottom-4 left-4 z-[60]',
        'flex items-center gap-2 px-3 py-2 rounded-lg',
        'bg-amber-500/90 text-black text-xs font-medium',
        'shadow-lg backdrop-blur-sm',
        'animate-in slide-in-from-left duration-300',
      )}
    >
      <Eye className="h-3.5 w-3.5 shrink-0" />
      <span>
        Preview: <strong>{MODE_LABELS[overrideMode]}</strong>
      </span>
      <button
        onClick={handleClear}
        className="ml-1 px-1.5 py-0.5 rounded bg-black/20 hover:bg-black/30 transition-colors text-[10px] uppercase tracking-wide"
      >
        Clear
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-0.5 p-0.5 rounded hover:bg-black/20 transition-colors"
        aria-label="Dismiss banner"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
